import "./RichTextLinkDialog.scss";

import { SbbButtonLink, SbbSecondaryButtonLink, SbbTransparentButtonLink } from "@sbb-esta/lyne-react/button";
import { defaultButtonSize } from "components/Lyne/LyneConstants";
import { LyneDialog } from "components/Lyne/LyneDialog";
import { LyneFieldError } from "components/Lyne/LyneFieldError";
import { LyneFormField } from "components/Lyne/LyneFormField";
import { useTranslations } from "providers";
import React, { useState } from "react";

const ALLOWED_URL_PREFIXES = ["https://", "http://", "mailto:", "/", "#"];

function isValidUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  return ALLOWED_URL_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
}

export interface RichTextLinkDialogProps {
  isOpen: boolean;
  isEditing: boolean;
  initialText: string;
  initialUrl: string;
  onSave: (text: string, url: string) => void;
  onRemove: () => void;
  onCancel: () => void;
}

export const RichTextLinkDialog: React.FC<RichTextLinkDialogProps> = ({
  isOpen,
  isEditing,
  initialText,
  initialUrl,
  onSave,
  onRemove,
  onCancel,
}) => {
  const i18n = useTranslations();
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState(initialUrl);
  const [urlError, setUrlError] = useState<string | null>(null);

  // State is initialised from props on mount. The parent resets this component by
  // changing its `key` prop each time the dialog opens, causing a fresh mount.

  const handleSave = () => {
    const trimmedUrl = url.trim();
    if (!isValidUrl(trimmedUrl)) {
      setUrlError(i18n.RichText_Link_Invalid_URL);
      return;
    }
    onSave(text.trim(), trimmedUrl);
  };

  return (
    <LyneDialog
      isOpen={isOpen}
      title={i18n.RichText_Link_Dialog_Title}
      content={
        <div className="richtext-link-dialog-fields">
          <LyneFormField name="link-text" size="s" label={i18n.RichText_Link_Text_Label} contextHelp={undefined}>
            <input value={text} autoComplete="off" onChange={(e) => setText(e.target.value)} />
          </LyneFormField>

          <LyneFormField name="link-url" required size="s" label={i18n.RichText_Link_URL_Label} contextHelp={undefined}>
            <input
              value={url}
              required
              autoComplete="off"
              className={urlError ? "sbb-invalid" : undefined}
              onChange={(e) => {
                setUrl(e.target.value);
                if (urlError) setUrlError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
            <LyneFieldError error={urlError} />
          </LyneFormField>
        </div>
      }
      actions={
        <>
          <SbbSecondaryButtonLink iconName="cross-medium" size={defaultButtonSize} onClick={onCancel}>
            {i18n.Actions_Cancel}
          </SbbSecondaryButtonLink>

          {isEditing && (
            <SbbTransparentButtonLink iconName="unlink-medium" size={defaultButtonSize} onClick={onRemove}>
              {i18n.RichText_Link_Remove}
            </SbbTransparentButtonLink>
          )}
          <div className="ml-auto">
            <SbbButtonLink iconName="tick-medium" size={defaultButtonSize} onClick={handleSave}>
              {i18n.Actions_Save}
            </SbbButtonLink>
          </div>
        </>
      }
    />
  );
};
